import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mic, BarChart3, Sparkles, Phone, ChevronRight, ChevronLeft, Check, MessageSquare, Target } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'framework', label: 'Your Framework' },
  { id: 'ready', label: 'Ready' },
];

export default function RepSetupWizard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [frameworks, setFrameworks] = useState([]);
  const [loadedFrameworks, setLoadedFrameworks] = useState(false);

  const totalSteps = STEPS.length;

  // Load frameworks when reaching step 2
  const loadFrameworks = async () => {
    if (loadedFrameworks) return;
    try {
      const { data } = await axios.get(`${API}/frameworks`);
      setFrameworks(data);
      setLoadedFrameworks(true);
    } catch (e) {
      console.error('Failed to load frameworks');
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      await loadFrameworks();
    }

    if (step === totalSteps - 1) {
      // Finish setup
      try {
        await axios.post(`${API}/auth/complete-setup`, {});
        setUser(prev => prev ? { ...prev, setup_completed: true } : prev);
        toast.success("You're all set! Start your first call.");
        navigate('/');
      } catch (e) {
        navigate('/');
      }
      return;
    }

    setStep(s => s + 1);
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/auth/complete-setup`, {});
      setUser(prev => prev ? { ...prev, setup_completed: true } : prev);
      navigate('/');
    } catch (e) {
      navigate('/');
    }
  };

  const defaultFramework = frameworks.find(f => f.is_default) || frameworks[0];

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="rep-setup-wizard">
      <div className="max-w-lg w-full">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${
              i === step ? 'bg-brand-primary w-8' : i < step ? 'bg-emerald-500' : 'bg-zinc-200'
            }`} />
          ))}
        </div>

        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="p-8">
            {/* Step 1: Welcome */}
            {step === 0 && (
              <div className="text-center space-y-6" data-testid="step-welcome">
                <div className="w-16 h-16 bg-brand-accent-muted rounded-full flex items-center justify-center mx-auto">
                  <img src="/pipeguide-logo.png" alt="PipeGuide" className="w-10 h-10 object-contain" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                    Welcome to PipeGuide, {user?.name?.split(' ')[0] || 'there'}!
                  </h2>
                  <p className="text-zinc-500 mt-2">
                    Your AI co-pilot for qualification calls. Let's do a quick 30-second walkthrough.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: How It Works */}
            {step === 1 && (
              <div className="space-y-6" data-testid="step-how-it-works">
                <h2 className="text-2xl font-bold text-zinc-900 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                  How PipeGuide works
                </h2>
                <div className="space-y-4">
                  {[
                    { icon: Phone, color: 'bg-brand-accent-muted text-brand-primary', title: 'Start a call', desc: 'Join via browser mic or invite the PipeGuide bot to your Zoom/Teams/Meet.' },
                    { icon: Sparkles, color: 'bg-violet-100 text-violet-600', title: 'AI analyses every 15 seconds', desc: 'PipeGuide scores your call in real-time and suggests the best questions to ask next.' },
                    { icon: BarChart3, color: 'bg-emerald-100 text-emerald-600', title: 'See your scores live', desc: 'Track Budget, Authority, Need, Timeline (or your custom categories) as they fill up.' },
                    { icon: MessageSquare, color: 'bg-amber-100 text-amber-600', title: 'Review after', desc: 'Get a full summary, PDF export, and push to your CRM with one click.' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${item.color}`}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-zinc-900">{item.title}</p>
                        <p className="text-sm text-zinc-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Your Framework */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-framework-overview">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                    Your qualification framework
                  </h2>
                  <p className="text-zinc-500 mt-1">Your admin has configured this framework for your team.</p>
                </div>
                {defaultFramework ? (
                  <div className="space-y-4">
                    <div className="bg-brand-muted border border-brand-accent rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-5 h-5 text-brand-primary" />
                        <span className="font-bold text-brand-primary">{defaultFramework.name}</span>
                      </div>
                      <p className="text-sm text-brand-primary">{defaultFramework.description}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-700 mb-2">Categories scored during your calls:</p>
                      <div className="flex flex-wrap gap-2">
                        {defaultFramework.categories?.map((cat, i) => (
                          <span key={i} className="px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-full text-sm font-medium text-zinc-700">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                    {defaultFramework.questions?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-zinc-700 mb-2">Sample questions PipeGuide may suggest:</p>
                        <div className="space-y-2">
                          {defaultFramework.questions.slice(0, 3).map((q, i) => (
                            <div key={i} className="flex gap-2 items-start text-sm">
                              <Sparkles className="w-3.5 h-3.5 text-brand-primary mt-0.5 flex-shrink-0" />
                              <span className="text-zinc-700">{q.question}</span>
                            </div>
                          ))}
                          {defaultFramework.questions.length > 3 && (
                            <p className="text-xs text-zinc-400 pl-5">+ {defaultFramework.questions.length - 3} more questions</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 text-zinc-500">
                    <Target className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                    <p>Your admin hasn't configured a custom framework yet. PipeGuide will use the default BANT framework.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Ready */}
            {step === 3 && (
              <div className="text-center space-y-6" data-testid="step-ready">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                    You're ready to go!
                  </h2>
                  <p className="text-zinc-500 mt-2">
                    Start your first call and PipeGuide will guide you through the qualification process in real-time.
                  </p>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-left">
                  <p className="text-sm font-medium text-zinc-700 mb-2">Quick tips:</p>
                  <ul className="space-y-1 text-sm text-zinc-600">
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Speak clearly — the transcript feeds the AI analysis</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Check the "Ask Next" section for suggested questions</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> Scores update every 15 seconds automatically</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> After the call, review your summary and push to CRM</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-200">
              <div>
                {step > 0 && (
                  <Button variant="outline" onClick={() => setStep(s => s - 1)} className="rounded-md" data-testid="rep-wizard-back">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleSkip} className="text-zinc-500" data-testid="rep-wizard-skip">
                  Skip
                </Button>
                <Button onClick={handleNext} className="rounded-md bg-brand-primary hover:bg-brand-hover" data-testid="rep-wizard-next">
                  {step === totalSteps - 1 ? (
                    <><Phone className="w-4 h-4 mr-2" /> Start My First Call</>
                  ) : (
                    <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
